import "moment/locale/he";
import moment from "moment/min/moment-with-locales";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ScreenHeader from "../../components/ScreenHeader";
import "./lessonCalendar.scss";

const DATE_PICKER_RANGE = 14; // same range as the V1 screen

const SunGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="4" fill="currentColor" />
    <path
      d="M12 2V4M12 20V22M4.93 4.93L6.34 6.34M17.66 17.66L19.07 19.07M2 12H4M20 12H22M4.93 19.07L6.34 17.66M17.66 6.34L19.07 4.93"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const MoonGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 12.8A9 9 0 1 1 11.2 3A7 7 0 0 0 21 12.8Z" fill="currentColor" />
  </svg>
);

// V2 lesson calendar (/selectRoomByDate) — presentational. Lesson data + booking
// navigation stay in the V1 screens/selectRoomByDate.jsx.
function LessonCalendar({ lessons, selectedDate, onSelectDate, onBook }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const days = Array.from({ length: DATE_PICKER_RANGE }, (_, offset) => {
    const m = moment().add(offset, "days").locale("he");
    return {
      date: m.format("DD.MM.YYYY"),
      dayNumber: m.format("D"),
      label: offset === 0 ? t("TODAY") : m.format("dddd"),
    };
  });

  // Same filtering expression as V1 (lessons of the selected day).
  const dayLessons = (lessons || []).filter((el) => el.LessonEndDate.split(" ")[0] === selectedDate);

  const startHour = (el) => parseInt(el.LessonStartDate.split(" ")[1], 10);

  return (
    <div className="lc-v2">
      <ScreenHeader title={t("ACTIVITY_ORDER")} onBack={() => navigate(-1)} />

      <div className="lc-v2-days">
        {days.map((day) => (
          <button
            type="button"
            key={day.date}
            className={`lc-v2-day${selectedDate === day.date ? " lc-v2-day--selected" : ""}`}
            onClick={() => onSelectDate(day.date)}
          >
            <span className="lc-v2-day-number">{day.dayNumber}</span>
            <span className="lc-v2-day-label">{day.label}</span>
          </button>
        ))}
      </div>

      <div className="lc-v2-list">
        {lessons == null ? null : dayLessons.length > 0 ? (
          dayLessons.map((el, i) => {
            // V1 semantics: overlay-muted when read-only-with-spots or already booked; sold out when 0 spots.
            const isReadOnlyOrBooked = (el.IsReadOnly && el.SlotsAvailable > 0) || el.IsUserBooked;
            const soldOut = el.SlotsAvailable === 0;
            const muted = isReadOnlyOrBooked || soldOut;
            const isEvening = startHour(el) >= 17 || startHour(el) < 5;

            return (
              <div
                key={`${el.LessonStartDate}-${el.RboxLessonID ?? i}`}
                className={`lc-v2-card${muted ? " lc-v2-card--muted" : ""}`}
                onClick={muted ? undefined : () => onBook(el)}
              >
                <div className="lc-v2-card-top">
                  {/* Icon first in DOM = rightmost in RTL, per design */}
                  <div className={`lc-v2-card-icon${muted ? " lc-v2-card-icon--muted" : isEvening ? " lc-v2-card-icon--moon" : " lc-v2-card-icon--sun"}`}>
                    {isEvening ? <MoonGlyph /> : <SunGlyph />}
                  </div>
                  <div className="lc-v2-card-texts">
                    <p className="lc-v2-card-time">
                      <span dir="ltr">
                        {el.LessonStartDate.split(" ")[1]} - {el.LessonEndDate.split(" ")[1]}
                      </span>
                    </p>
                    <p className="lc-v2-card-lesson">{el.LessonName}</p>
                  </div>
                </div>
                <div className="lc-v2-card-bottom">
                  <p className="lc-v2-card-coach">{el.CoachName}</p>
                  {soldOut ? (
                    <span className="lc-v2-chip lc-v2-chip--full">{t("NO_SPOTS_LEFT")}</span>
                  ) : el.IsUserBooked ? (
                    <span className="lc-v2-chip lc-v2-chip--full">{t("LESSON_ALREADY_BOOKED")}</span>
                  ) : isReadOnlyOrBooked ? (
                    <span className="lc-v2-chip lc-v2-chip--full">{t("LESSON_NOT_BOOKABLE")}</span>
                  ) : (
                    <span className="lc-v2-chip">
                      {el.SlotsAvailable} {t("AVAILABLE_SPOTS")}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="lc-v2-empty">
            <p className="lc-v2-empty-title">{t("NO_LESSONS_TODAY")}</p>
            <p className="lc-v2-empty-body">{t("CHECK_OTHER_DAYS")}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default LessonCalendar;
